import type { ReactNode } from "react";
import { ModuleBrandLogo } from "@itaas/core/branding/ModuleBrandLogo";
import { COMPANY_NAME } from "@itaas/core/branding/branding";

export interface SuiteAuthShellProps {
  children: ReactNode;
  /** login = formulaire centré ; modules = contenu pleine largeur */
  layout?: "login" | "modules";
  /** Panneau gauche — titre principal */
  heroTitle: ReactNode;
  /** Panneau gauche — sous-titre */
  heroSubtitle?: ReactNode;
  /** Contenu optionnel sous le hero (stats, badges…) */
  heroExtra?: ReactNode;
  /** Afficher le logo en haut du formulaire (défaut login). */
  showFormBrand?: boolean;
  /** Afficher le nom commercial à côté du logo (défaut : false sur login). */
  showFormBrandName?: boolean;
  /** Nom commercial du logo formulaire */
  brandName?: string;
  /** Pied de page du panneau droit */
  footer?: ReactNode;
}

export function SuiteAuthShell({
  children,
  layout = "login",
  heroTitle,
  heroSubtitle,
  heroExtra,
  showFormBrand = true,
  showFormBrandName = false,
  brandName = "ITaaS Suite",
  footer,
}: SuiteAuthShellProps) {
  const year = new Date().getFullYear();
  const defaultFooter = (
    <p className="suite-auth-footer mb-0">
      © {year} {COMPANY_NAME}. Tous droits réservés.
    </p>
  );

  return (
    <div className={`suite-auth-page suite-auth-page--${layout}`}>
      <div className="row g-0 min-vh-100">
        <div className="col-lg-5 d-none d-lg-flex">
          <aside className="suite-auth-hero" aria-label="Présentation ITaaS Suite">
            <div className="suite-auth-hero__decor" aria-hidden>
              <span className="suite-auth-hero__orb suite-auth-hero__orb--1" />
              <span className="suite-auth-hero__orb suite-auth-hero__orb--2" />
              <span className="suite-auth-hero__orb suite-auth-hero__orb--3" />
            </div>
            <div className="suite-auth-hero__card">
              <h1 className="suite-auth-hero__title">{heroTitle}</h1>
              {heroSubtitle ? (
                <p className="suite-auth-hero__subtitle">{heroSubtitle}</p>
              ) : null}
              {heroExtra}
            </div>
          </aside>
        </div>

        <div className="col-lg-7 col-12">
          <div className="suite-auth-panel">
            {!showFormBrand ? (
              <div className="suite-auth-mobile-brand d-lg-none text-center">
                <ModuleBrandLogo name={brandName} layout="stacked" />
              </div>
            ) : null}
            <div className="suite-auth-panel__inner">
              {showFormBrand ? (
                <div className="suite-auth-brand text-center">
                  <ModuleBrandLogo
                    name={brandName}
                    layout="stacked"
                    showName={showFormBrandName}
                    className="suite-auth-brand__logo"
                  />
                  {!showFormBrandName ? (
                    <p className="suite-auth-brand__caption mb-0">{brandName}</p>
                  ) : null}
                </div>
              ) : null}
              {showFormBrand ? <div className="suite-auth-form-divider" aria-hidden /> : null}
              {children}
            </div>
            <div className="suite-auth-panel__footer text-center">
              {footer ?? defaultFooter}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
